import { APP_CONFIG } from 'mangools-commons/dist/configs/app';
import ErrorCodes from 'mangools-commons/dist/constants/ErrorCodes';
import MangoolsClients from 'mangools-commons/dist/constants/MangoolsClients';
import HumanService from 'mangools-commons/dist/services/HumanService';

const TRACKINGS_ENDPOINT = '/trackings';
const REFRESH_ENDPOINT = 'tracked-keywords/refresh';

// Response shape of POST /trackings/:id/tracked-keywords/refresh (api.mangools.com,
// routers/serpwatcher/onDemandRefresh.js): the same five keys on the happy path and on the
// all-skipped early return.
//   { refreshed_count, skipped_count, skipped_ids, estimate_seconds, remaining }
class TrackingKeywordsRefreshSource {
    static refresh(accessToken, { trackingId, keywordIds }) {
        const requestURL = `${APP_CONFIG.API_HOST}${APP_CONFIG.API_PATH}${TRACKINGS_ENDPOINT}/${trackingId}/${REFRESH_ENDPOINT}`;

        return fetch(requestURL, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'X-Access-Token': accessToken || '',
                // Without this the API bills the call as an external API request, charging
                // `count * user.api_partial_multiplier` instead of `count`.
                'X-Mangools-Client': MangoolsClients.APP,
                // The route checks the signature (Auth.checkSignature), so a call that claims to be
                // the app has to prove it, like the other sources here do.
                'X-Human-Token': HumanService.gen(),
            },
            body: JSON.stringify({ tracked_keyword_ids: keywordIds }),
        })
            .then(response => {
                if (response.ok) {
                    return response
                        .json()
                        .then(data => {
                            // The API reports which keywords it skipped, not which it queued, so the
                            // refreshed ids are the requested selection minus the skipped ones. A Set
                            // keeps this linear - the endpoint accepts up to 5000 ids per request.
                            const skippedIds = (data.skipped_ids ?? []).map(String);
                            const skipped = new Set(skippedIds);

                            return {
                                error: false,
                                payload: {
                                    refreshedCount: data.refreshed_count ?? 0,
                                    skippedCount: data.skipped_count ?? 0,
                                    estimateSeconds: data.estimate_seconds ?? null,
                                    skippedIds,
                                    refreshedKeywordIds: keywordIds.filter(id => !skipped.has(id)),
                                    remaining: data.remaining ?? null,
                                },
                            };
                        })
                        .catch(ex => ({
                            error: true,
                            payload: {
                                requestURL,
                                status: ErrorCodes.PARSE_ERROR,
                                text: ex.toString(),
                            },
                        }));
                } else {
                    return response.json().then(e => ({
                        error: true,
                        payload: {
                            requestURL,
                            status: response.status,
                            text: e.error ? e.error.message : null,
                            type: e.error ? e.error.type : null,
                            // A 429 carries `{ requested, remaining }` here, both counted in scans.
                            details: e.error?.details ?? null,
                        },
                    }));
                }
            })
            .catch(ex => ({
                error: true,
                payload: {
                    requestURL,
                    status: ErrorCodes.FETCH_ERROR,
                    text: ex.toString(),
                },
            }));
    }
}

export default TrackingKeywordsRefreshSource;
